// Filter Registry - Central export for all filter configurations
// This is the single source of truth for both frontend and backend

// Re-export types
export * from './types';

// Re-export factory functions
export * from './factories';

// Re-export report filter configurations
export { REPORT_FILTER_CONFIGS } from './reporting';

// Import entity filter registries
import { accessLogsFilters } from './access-logs';
import { automationFilters } from './automation';
import { callHistoryFilters } from './call-history';
import { clientFilters } from './client';
import { clientContactFilters } from './client-contact';
import { clientVisitFilters } from './client-visit';
import { clientItemNumberFilters } from './client-item-number';
import { itemCrossReferenceFilters } from './item-cross-reference';
import { competitorFilters } from './competitor';
import { jobFilters } from './job';
import { conversationFilters } from './conversation';
import { distributionFilters } from './distribution';
import { ecommerceFilters } from './ecommerce';
import { emailFilters } from './email';
import { formSubmissionFilters } from './form-submissions';
import { formFilters } from './forms';
import { invoiceFilters } from './invoice';
import { issueFilters } from './issue';
import { itemFilters } from './item';
import { leadEligibilityFilters } from './lead-eligibility';
import { licenseFilters } from './license';
import { licenseAuditFilters } from './license-audit';
import { licenseOrderCopyFilters } from './license-order-copies';
import { licenseDisciplinaryFilters } from './license-disciplinary';
import { licenseDiscrepanciesFilters } from './license-discrepancies';
import { licenseExportLogFilters } from './license-export-log';
import { licenseLinksFilters } from './license-links';
import { licenseMatchFilters } from './license-match';
import { licenseMatchChangeFilters } from './license-match-changes';
import { opportunityFilters } from './opportunity';
import { orderFilters } from './order';
import { paymentFilters } from './payment';
import { promoFilters } from './promo';
import { purchaseOrderFilters } from './purchase-order';
import { purchaseOrderReceiptFilters } from './purchase-order-receipt';
import { quoteFilters } from './quote';
import { leadFinderFilters } from './lead-finder';
import { licenseExclusionFilters } from './license-exclusion';
import { registryFilters } from './registry';
import { routeFilters } from './route';
import { salesTripFilters } from './sales-trip';
import { sharedFilters } from './shared';
import { sourcingRequestFilters } from './sourcing-request';
import { statementFilters } from './statement';
import { taskFilters } from './task';
import { teamFilters } from './team';
import { vendorFilters } from './vendor';
import { warehouseFilters } from './warehouse';

import type { FilterRegistry } from './types';
import { teamListFilters } from './team-lists';

/**
 * Merge multiple filter registries with duplicate key detection
 * Throws an error if any duplicate keys are found
 */
function mergeWithDuplicateCheck(
	...registries: Array<FilterRegistry>
): FilterRegistry {
	const result: FilterRegistry = {};

	for (const registry of registries) {
		for (const key of Object.keys(registry)) {
			if (result[key] != null) {
				throw new Error(
					`Duplicate filter key detected: "${key}". Each filter must have a unique key across all registries.`,
				);
			}

			const filterConfig = registry[key];

			if (filterConfig != null) {
				result[key] = filterConfig;
			}
		}
	}

	return result;
}

/**
 * Shared Filter Registry
 * Used by both frontend and backend to ensure consistency
 * This is the SINGLE SOURCE OF TRUTH for all filter definitions
 */
export const SHARED_FILTER_REGISTRY: FilterRegistry = mergeWithDuplicateCheck(
	sharedFilters,
	teamListFilters,
	clientFilters,
	clientContactFilters,
	clientVisitFilters,
	competitorFilters,
	clientItemNumberFilters,
	itemCrossReferenceFilters,
	jobFilters,
	taskFilters,
	teamFilters,
	leadEligibilityFilters,
	formSubmissionFilters,
	formFilters,
	orderFilters,
	invoiceFilters,
	quoteFilters,
	opportunityFilters,
	issueFilters,
	itemFilters,
	licenseFilters,
	licenseMatchFilters,
	licenseMatchChangeFilters,
	licenseAuditFilters,
	licenseOrderCopyFilters,
	licenseDisciplinaryFilters,
	licenseDiscrepanciesFilters,
	licenseLinksFilters,
	sourcingRequestFilters,
	callHistoryFilters,
	automationFilters,
	accessLogsFilters,
	emailFilters,
	conversationFilters,
	vendorFilters,
	warehouseFilters,
	paymentFilters,
	ecommerceFilters,
	purchaseOrderFilters,
	purchaseOrderReceiptFilters,
	routeFilters,
	salesTripFilters,
	promoFilters,
	registryFilters,
	leadFinderFilters,
	licenseExclusionFilters,
	// PD-17390: Compliance > Sent to ERP.
	licenseExportLogFilters,
	distributionFilters,
	statementFilters,
);
